import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/Layout';
import { drivers } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  UserCheck, 
  UserX, 
  Phone, 
  Mail, 
  Bike, 
  Car, 
  Wallet,
  TrendingUp,
  Package,
  Smartphone,
  Key
} from 'lucide-react';

const VEHICLE_TYPES = [
  { value: 'MOTO', label: 'Moto', icon: Bike },
  { value: 'BIKE', label: 'Bicicleta', icon: Bike },
  { value: 'CARRO', label: 'Carro', icon: Car },
  { value: 'A_PE', label: 'A pé', icon: Package },
];

const initialFormData = {
  name: '',
  phone: '',
  cpf: '',
  email: '',
  vehicle_type: 'MOTO',
  vehicle_plate: '',
  vehicle_model: '',
  delivery_fee_rate: 5.0,
  commission_rate: 0.0,
};

export function DriversPage() {
  const [driversList, setDriversList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingDriver, setDeletingDriver] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, inactive
  
  // App Account States
  const [appAccountDialogOpen, setAppAccountDialogOpen] = useState(false);
  const [selectedDriverForApp, setSelectedDriverForApp] = useState(null);
  const [appUsername, setAppUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Reset password state
  const [resetPwdDialogOpen, setResetPwdDialogOpen] = useState(false);
  const [selectedDriverForReset, setSelectedDriverForReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPwd, setResettingPwd] = useState(false);

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const response = await drivers.list();
      setDriversList(response.data);
    } catch (error) {
      toast.error('Erro ao carregar motoboys');
    } finally {
      setLoading(false);
    }
  };

  const filteredDrivers = driversList.filter(driver => {
    if (filter === 'active') return driver.is_active;
    if (filter === 'inactive') return !driver.is_active;
    return true;
  });

  const handleOpenDialog = (driver = null) => {
    if (driver) {
      setEditingDriver(driver);
      setFormData({
        name: driver.name || '',
        phone: driver.phone || '',
        cpf: driver.cpf || '',
        email: driver.email || '',
        vehicle_type: driver.vehicle_type || 'MOTO',
        vehicle_plate: driver.vehicle_plate || '',
        vehicle_model: driver.vehicle_model || '',
        delivery_fee_rate: driver.delivery_fee_rate ?? 5.0,
        commission_rate: driver.commission_rate ?? 0.0,
      });
    } else {
      setEditingDriver(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDriver(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Nome e telefone são obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      if (editingDriver) {
        await drivers.update(editingDriver.id, formData);
        toast.success('Motoboy atualizado com sucesso');
      } else {
        await drivers.create(formData);
        toast.success('Motoboy cadastrado com sucesso');
      }
      handleCloseDialog();
      loadDrivers();
    } catch (error) {
      toast.error(editingDriver ? 'Erro ao atualizar motoboy' : 'Erro ao cadastrar motoboy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleAvailability = async (driver) => {
    try {
      await drivers.toggleAvailability(driver.id);
      toast.success(driver.is_available ? 'Motoboy marcado como indisponível' : 'Motoboy marcado como disponível');
      loadDrivers();
    } catch (error) {
      toast.error('Erro ao alterar disponibilidade');
    }
  };

  const handleDeleteClick = (driver) => {
    setDeletingDriver(driver);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDriver) return;
    
    try {
      const resp = await drivers.delete(deletingDriver.id);
      const mode = resp?.data?.mode;
      if (mode === 'soft') {
        toast.success('Motoboy inativado (possui histórico de entregas)');
      } else {
        toast.success('Motoboy removido com sucesso');
      }
      setDeleteDialogOpen(false);
      setDeletingDriver(null);
      loadDrivers();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Erro ao remover motoboy';
      toast.error(msg);
    }
  };

  // App Account Functions
  const handleOpenAppAccountDialog = (driver) => {
    setSelectedDriverForApp(driver);
    setAppUsername(driver.phone?.replace(/\D/g, '') || '');
    setAppPassword('');
    setAppAccountDialogOpen(true);
  };

  const handleCreateAppAccount = async () => {
    if (!selectedDriverForApp || !appUsername || !appPassword) {
      toast.error('Preencha usuário e senha');
      return;
    }

    if (appPassword.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    setCreatingAccount(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/driver-app/auth/admin/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driver_id: selectedDriverForApp.id,
          username: appUsername,
          password: appPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error === 'account_already_exists' ? 'Conta já existe para este motoboy' : data.error);
      }

      toast.success('Conta do app criada com sucesso!');
      setAppAccountDialogOpen(false);
      setSelectedDriverForApp(null);
      setAppUsername('');
      setAppPassword('');
    } catch (error) {
      toast.error(error.message || 'Erro ao criar conta');
    } finally {
      setCreatingAccount(false);
    }
  };


  const handleOpenResetPwdDialog = (driver) => {
    setSelectedDriverForReset(driver);
    setNewPassword('');
    setResetPwdDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedDriverForReset || !newPassword) {
      toast.error('Informe a nova senha');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }
    setResettingPwd(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/driver-app/auth/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driver_id: selectedDriverForReset.id,
          password: newPassword
        })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error === 'account_not_found' ? 'Este motoboy ainda nao tem conta no app. Crie primeiro.' : (data.error || 'Erro ao redefinir senha'));
      }
      toast.success('Senha redefinida com sucesso! O motoboy precisara entrar novamente no app.');
      setResetPwdDialogOpen(false);
      setSelectedDriverForReset(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e.message || 'Erro ao redefinir senha');
    } finally {
      setResettingPwd(false);
    }
  };

  const getVehicleIcon = (type) => {
    const vehicle = VEHICLE_TYPES.find(v => v.value === type);
    return vehicle?.icon || Bike;
  };

  // Calcular estatísticas
  const stats = {
    total: driversList.length,
    active: driversList.filter(d => d.is_active).length,
    available: driversList.filter(d => d.is_available).length,
    totalDeliveries: driversList.reduce((sum, d) => sum + (d.total_deliveries || 0), 0),
    totalBalance: driversList.reduce((sum, d) => sum + (d.balance || 0), 0),
  };

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="drivers-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Gestão de Motoboys
            </h1>
            <p className="text-slate-600">Cadastre e gerencie sua equipe de entregadores</p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#FF6B6B] hover:bg-[#FF7A5A]"
            data-testid="add-driver-btn"
          >
            <Plus size={20} className="mr-2" />
            Novo Motoboy
          </Button>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <UserCheck size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Disponíveis</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.available}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Bike size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Entregas</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalDeliveries}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Wallet size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Saldo Total</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.totalBalance)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <TrendingUp size={20} className="text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ativos</p>
                <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-2">
          {['all', 'active', 'inactive'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-[#FF6B6B] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${f}-btn`}
            >
              {f === 'all' ? 'Todos' : f === 'active' ? 'Ativos' : 'Inativos'}
            </button>
          ))}
        </div>

        {/* Lista de Motoboys */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Carregando...</div>
          ) : filteredDrivers.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Nenhum motoboy cadastrado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Motoboy</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Contato</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Veículo</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Taxa/Entrega</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Entregas</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Saldo</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDrivers.map((driver) => {
                    const VehicleIcon = getVehicleIcon(driver.vehicle_type);
                    return (
                      <tr
                        key={driver.id}
                        className="bg-white hover:bg-slate-50 transition-colors border-b border-slate-100"
                        data-testid={`driver-row-${driver.id}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              driver.is_available ? 'bg-emerald-100' : 'bg-slate-100'
                            }`}>
                              <VehicleIcon 
                                size={20} 
                                className={driver.is_available ? 'text-emerald-600' : 'text-slate-400'} 
                              />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{driver.name}</p>
                              {driver.cpf && (
                                <p className="text-xs text-slate-400">CPF: {driver.cpf}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Phone size={14} />
                              <span>{driver.phone}</span>
                            </div>
                            {driver.email && (
                              <div className="flex items-center gap-1 text-sm text-slate-400">
                                <Mail size={14} />
                                <span>{driver.email}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p className="text-slate-900 font-medium">
                              {VEHICLE_TYPES.find(v => v.value === driver.vehicle_type)?.label || driver.vehicle_type}
                            </p>
                            {driver.vehicle_plate && (
                              <p className="text-slate-400">{driver.vehicle_plate}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-slate-900">
                            {formatCurrency(driver.delivery_fee_rate)}
                          </span>
                          {driver.commission_rate > 0 && (
                            <p className="text-xs text-slate-400">+{driver.commission_rate}% comissão</p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-slate-900">{driver.total_deliveries || 0}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-bold ${driver.balance > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {formatCurrency(driver.balance || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              driver.is_active 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {driver.is_active ? 'Ativo' : 'Inativo'}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              driver.is_available 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-slate-100 text-slate-500'
                            }`}>
                              {driver.is_available ? 'Disponível' : 'Indisponível'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleAvailability(driver)}
                              className={`p-2 rounded-lg transition-colors ${
                                driver.is_available 
                                  ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                                  : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                              }`}
                              title={driver.is_available ? 'Marcar indisponível' : 'Marcar disponível'}
                              data-testid={`toggle-availability-${driver.id}`}
                            >
                              {driver.is_available ? <UserX size={18} /> : <UserCheck size={18} />}
                            </button>
                            <button
                              onClick={() => handleOpenDialog(driver)}
                              className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                              title="Editar"
                              data-testid={`edit-driver-${driver.id}`}
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              onClick={() => handleOpenAppAccountDialog(driver)}
                              className="p-2 rounded-lg bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
                              title="Criar conta do App"
                              data-testid={`app-account-${driver.id}`}
                            >
                              <Smartphone size={18} />
                            </button>
                            <button
                              onClick={() => handleOpenResetPwdDialog(driver)}
                              className="p-2 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                              title="Redefinir senha do app"
                              data-testid={`reset-pwd-${driver.id}`}
                            >
                              <Key size={18} />
                            </button>

                            <button
                              onClick={() => handleDeleteClick(driver)}
                              className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                              title="Remover"
                              data-testid={`delete-driver-${driver.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialog de Criação/Edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingDriver ? 'Editar Motoboy' : 'Novo Motoboy'}</DialogTitle>
            <DialogDescription>
              {editingDriver 
                ? 'Atualize os dados do motoboy' 
                : 'Preencha os dados para cadastrar um novo motoboy'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                  data-testid="driver-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Telefone *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  data-testid="driver-phone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                  data-testid="driver-cpf-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  data-testid="driver-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="vehicle_type">Tipo de Veículo</Label>
                <Select
                  value={formData.vehicle_type}
                  onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                >
                  <SelectTrigger data-testid="driver-vehicle-type-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="vehicle_plate">Placa</Label>
                <Input
                  id="vehicle_plate"
                  value={formData.vehicle_plate}
                  onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value.toUpperCase() })}
                  placeholder="ABC-1234"
                  data-testid="driver-vehicle-plate-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="vehicle_model">Modelo do Veículo</Label>
                <Input
                  id="vehicle_model"
                  value={formData.vehicle_model}
                  onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                  placeholder="Ex: Honda CG 160"
                  data-testid="driver-vehicle-model-input"
                />
              </div>
              
              <div>
                <Label htmlFor="delivery_fee_rate">Taxa por Entrega (R$)</Label>
                <Input
                  id="delivery_fee_rate"
                  type="number"
                  step="0.50"
                  min="0"
                  value={formData.delivery_fee_rate}
                  onChange={(e) => setFormData({ ...formData, delivery_fee_rate: parseFloat(e.target.value) || 0 })}
                  data-testid="driver-delivery-fee-input"
                />
              </div>
              
              <div>
                <Label htmlFor="commission_rate">Comissão (%)</Label>
                <Input
                  id="commission_rate"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={formData.commission_rate}
                  onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) || 0 })}
                  data-testid="driver-commission-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-[#FF6B6B] hover:bg-[#FF7A5A]"
                disabled={submitting}
                data-testid="submit-driver-btn"
              >
                {submitting ? 'Salvando...' : editingDriver ? 'Atualizar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o motoboy{' '}
              <strong>{deletingDriver?.name}</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              data-testid="confirm-delete-btn"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Criar Conta do App */}
      <Dialog open={appAccountDialogOpen} onOpenChange={setAppAccountDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone size={20} className="text-purple-600" />
              Criar Conta do App
            </DialogTitle>
            <DialogDescription>
              Crie uma conta para <strong>{selectedDriverForApp?.name}</strong> acessar o aplicativo de entregas.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="app-username">Usuário</Label>
              <Input
                id="app-username"
                value={appUsername}
                onChange={(e) => setAppUsername(e.target.value)}
                placeholder="Ex: joao.silva"
                className="bg-white"
              />
              <p className="text-xs text-slate-500">Use o telefone ou um nome de usuário único</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="app-password">Senha</Label>
              <Input
                id="app-password"
                type="password"
                value={appPassword}
                onChange={(e) => setAppPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="bg-white"
              />
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-700">
                <strong>URL do App:</strong><br />
                <code className="text-xs bg-purple-100 px-1 rounded">https://pdv.hitgrill.com.br/driver-app</code>
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppAccountDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateAppAccount}
              disabled={creatingAccount || !appUsername || !appPassword}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingAccount ? 'Criando...' : 'Criar Conta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Redefinir Senha do App */}
      <Dialog open={resetPwdDialogOpen} onOpenChange={setResetPwdDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key size={20} className="text-amber-600" />
              Redefinir Senha do App
            </DialogTitle>
            <DialogDescription>
              Defina uma nova senha para <strong>{selectedDriverForReset?.name}</strong> acessar o aplicativo.
              Todas as sessoes ativas serao encerradas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-app-password">Nova senha</Label>
              <Input
                id="new-app-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimo 6 caracteres"
                className="bg-white"
                autoComplete="new-password"
                data-testid="reset-pwd-input"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Apos confirmar, o motoboy sera deslogado do app e precisara entrar com a nova senha.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwdDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resettingPwd || !newPassword}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reset-pwd-submit"
            >
              {resettingPwd ? 'Salvando...' : 'Redefinir Senha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </MainLayout>
  );
}
